import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  FileText, 
  Building, 
  Users, 
  Heart, 
  Shield, 
  Search,
  Download,
  Clock,
  DollarSign,
  Star,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import AIAssistant from "../components/AIAssistant";

const FormsLibrary = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [downloadingTemplate, setDownloadingTemplate] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState({});

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["All", "Family Law", "Business & Corporate", "Wills, Estates & Probate", "Criminal Record / Expungement"];

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getIconForCategory = (category) => {
    switch (category) {
      case "Family Law": return Heart;
      case "Business & Corporate": return Building;
      case "Wills, Estates & Probate": return FileText;
      case "Criminal Record / Expungement": return Shield;
      default: return FileText;
    }
  };

  const handleDownloadTemplate = async (service) => {
    setDownloadingTemplate(service.id);
    setDownloadStatus({...downloadStatus, [service.id]: 'downloading'});
    
    try {
      // Generate basic template document
      const response = await axios.post(`${API}/api/documents/generate-template`, {
        service_id: service.id,
        service_title: service.title,
        service_category: service.category,
        template_type: 'basic'
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${service.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_template.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setDownloadStatus({...downloadStatus, [service.id]: 'success'});
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setDownloadStatus({...downloadStatus, [service.id]: null});
      }, 3000);

    } catch (error) {
      console.error('Error downloading template:', error);
      setDownloadStatus({...downloadStatus, [service.id]: 'error'});
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setDownloadStatus({...downloadStatus, [service.id]: null});
      }, 3000);
    } finally {
      setDownloadingTemplate(null);
    }
  };

  const handleCreateCustom = (service) => {
    // Navigate to questionnaire page for custom document creation
    navigate(`/questionnaire/${service.id}`);
  };

  const getDownloadButtonContent = (service) => {
    const status = downloadStatus[service.id];
    const isDownloading = downloadingTemplate === service.id;

    if (isDownloading) {
      return (
        <>
          <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
          Downloading...
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
          Downloaded!
        </>
      );
    }

    if (status === 'error') {
      return (
        <>
          <AlertCircle className="w-4 h-4 mr-2 text-red-600" />
          Try Again
        </>
      );
    }

    return (
      <>
        <Download className="w-4 h-4 mr-2" />
        Download Template
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Legal Forms Library
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Browse our comprehensive collection of legal documents. Download instantly or create customized versions with our AI-guided process.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search legal forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg border-gray-300 rounded-lg"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 
                    "bg-yellow-500 hover:bg-yellow-600 text-black" : 
                    "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Forms Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading forms...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const IconComponent = getIconForCategory(service.category);
                return (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow border-gray-200">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="p-2 bg-yellow-100 rounded-lg">
                            <IconComponent className="w-5 h-5 text-yellow-600" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {service.category}
                          </Badge>
                        </div>
                        {service.popular && (
                          <Badge className="bg-yellow-500 text-black">
                            <Star className="w-3 h-3 mr-1" />
                            Popular
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg font-semibold text-gray-800">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {service.duration}
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-800">${service.price}</span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="font-medium text-gray-800 mb-2">Included Documents:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {service.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1 h-1 bg-yellow-500 rounded-full mr-2"></span>
                              {feature}
                            </li>
                          ))}
                          {service.features.length > 3 && (
                            <li className="text-gray-500 text-xs">
                              +{service.features.length - 3} more documents
                            </li>
                          )}
                        </ul>
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={`flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 ${
                            downloadStatus[service.id] === 'success' ? 'border-green-500 text-green-700' :
                            downloadStatus[service.id] === 'error' ? 'border-red-500 text-red-700' : ''
                          }`}
                          onClick={() => handleDownloadTemplate(service)}
                          disabled={downloadingTemplate === service.id}
                        >
                          {getDownloadButtonContent(service)}
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={() => handleCreateCustom(service)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Create Custom
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {!loading && filteredServices.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No forms found</h3>
              <p className="text-gray-600">Try adjusting your search or category filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* AI Assistant Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Need Something Custom?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Our AI Legal Assistant can help you find the perfect document or answer any legal questions
            </p>
          </div>
          <AIAssistant />
        </div>
      </section>
    </div>
  );
};

export default FormsLibrary;